import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";

const STEPS = [
  {
    n: "01",
    title: "Standortpotenzial anfragen",
    text: "Sie senden erste Informationen zu Standort, Fläche, Zielgruppe und Nutzungsidee.",
    result: "Wir erkennen, welches Systemprofil grundsätzlich relevant sein könnte.",
  },
  {
    n: "02",
    title: "Erstgespräch",
    text: "Wir verstehen Ausgangslage, Ziel, Standortkontext und gewünschte Nutzung.",
    result: "Aus einer ersten Idee wird eine strukturierte Ausgangslage.",
  },
  {
    n: "03",
    title: "Produktfit prüfen",
    text: "PVRA, AR_ENA, PINE VR, Hotel VR oder PINE Places werden anhand Ihres Standorts eingeordnet.",
    result:
      "Sie erhalten eine erste Richtung, welches Systemprofil sinnvoll weiter geprüft werden sollte.",
  },
  {
    n: "04",
    title: "Standortprüfung",
    text: "Fläche, Raumlogik, Infrastruktur, Betreiberstruktur, Gästewege und Zielgruppe werden genauer bewertet.",
    result:
      "Es wird sichtbar, ob und unter welchen Voraussetzungen ein System grundsätzlich passen kann.",
  },
  {
    n: "05",
    title: "Konzeptvariante",
    text: "Aus der Prüfung entsteht eine erste Richtung für Angebot, Nutzung, Erlebnislogik und nächsten Umsetzungsschritt.",
    result: "Sie erhalten eine greifbare Systemrichtung statt einer losen Idee.",
  },
  {
    n: "06",
    title: "Planung und Umsetzung",
    text: "Nach Freigabe folgen technische Planung, Vorbereitung, Schulung, Installation und Launch.",
    result:
      "Aus dem Systemprofil wird ein vorbereiteter Erlebnisstandort oder digitaler Markenraum.",
  },
];

export default function Ablauf() {
  return (
    <>
      <SEO
        title="Ablauf – Vom Standortpotenzial zum passenden Systemprofil"
        description="So führt PINE Systems Interessenten von der Standortanfrage über Produktfit und Standortprüfung bis zur Konzeptvariante und Umsetzung."
      />
      <section className="relative border-b border-[rgba(255,255,255,0.08)] overflow-hidden">
        <div className="absolute inset-0 grid-overlay opacity-30" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-12 md:pt-36 md:pb-20">
          <div className="overline">Ablauf</div>
          <h1 className="font-display text-4xl md:text-6xl uppercase mt-4 max-w-4xl leading-[1.05]">
            Vom ersten Interesse <br />
            zum passenden <span className="text-[#18E096]">Systemprofil.</span>
          </h1>
          <p className="text-[#AEB7B7] mt-6 max-w-3xl text-base md:text-lg leading-relaxed">
            Der Weg zu einem PINE Systems Standort beginnt nicht mit einer Kaufentscheidung. Er
            beginnt mit Verstehen: Standort, Zielgruppe, Fläche, Nutzungsidee, Betreiberstruktur
            und Customer Journey.
          </p>
        </div>
      </section>

      <section>
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <ol className="relative">
            <div
              className="absolute left-6 md:left-10 top-0 bottom-0 w-px bg-[rgba(255,255,255,0.12)]"
              aria-hidden
            />
            {STEPS.map((s, i) => (
              <li
                key={s.n}
                className="relative pl-16 md:pl-24 pb-14 last:pb-0"
                data-testid={`ablauf-step-${i}`}
              >
                <div className="absolute left-0 top-0 w-12 md:w-20 flex items-start justify-center">
                  <span className="font-display text-2xl md:text-3xl text-[#18E096] bg-[#050607] px-2">
                    {s.n}
                  </span>
                </div>
                <h3 className="font-display text-xl md:text-2xl uppercase">{s.title}</h3>
                <p className="text-[#AEB7B7] mt-3 max-w-2xl leading-relaxed">{s.text}</p>
                <div className="mt-5 max-w-2xl border-l-2 border-[#18E096] pl-4">
                  <div className="overline mb-1">Ergebnis</div>
                  <p className="text-white text-sm md:text-base leading-relaxed">{s.result}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="mt-8 surface p-8">
            <h3 className="font-display text-xl md:text-2xl uppercase">
              Lassen Sie uns den ersten Schritt prüfen.
            </h3>
            <p className="text-[#AEB7B7] mt-3 max-w-2xl">
              Der nächste Schritt ist keine Kaufentscheidung, sondern eine strukturierte
              Einschätzung Ihres Standortpotenzials.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/standort-pruefen" className="btn-primary" data-testid="ablauf-cta">
                Standortpotenzial prüfen <ArrowUpRight size={16} strokeWidth={1.5} />
              </Link>
              <Link to="/kontakt" className="btn-secondary">
                Erstgespräch anfragen
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
